import type { CasaNativaAdminSnapshot } from "./CasaNativaClient";

type PreorderProjection = {
  schemaVersion: 1;
  coordination: "orders-linked";
  tableReservation: false;
  slot: {
    id: string;
    serviceAt: string;
    cutoffAt: string;
    enabled: boolean;
    revision: number;
    capacity: { maxOrders: number | null; maxUnits: number | null };
    decision: {
      status: string;
      reason: string;
      activeOrders: number;
      activeUnits: number;
      remainingOrders: number | null;
      remainingUnits: number | null;
    };
  };
  reservation: {
    id: string;
    orderId: string;
    status: string;
    units: number;
    itemRefs: readonly { sourceModuleKey: string; sourceItemId: string; sourceVariantId?: string | null }[];
    heldAt: string;
    expiresAt: string;
    confirmedAt: string | null;
    revision: number;
  };
  order: {
    id: string;
    customer: string;
    fulfillmentType: string;
    status: string;
    totalMinor: number;
  };
};

type OperationalFact = {
  factKey: string;
  status: string;
  reason?: string;
  value?:
    | { kind: "count"; value: number; unit: string }
    | { kind: "quantity"; amount: number; scale: number; unit: string };
  sourceObservation: { sourceOwner: string };
  evidenceRefs: readonly string[];
};

type CostingEvidence = {
  status: string;
  kind: string;
  recipeVersionId?: string;
  currency?: string;
  totalMinor?: number;
  reason?: string;
  sourceRefs: readonly string[];
};

type ManagementProjection = {
  schemaVersion: 1;
  window: { startInclusive: string; endExclusive: string };
  operationalFacts: readonly OperationalFact[];
  costingEvidence: readonly CostingEvidence[];
  gaps: readonly { sourceOwner: string; key: string; reason: string }[];
  attentionItems: readonly { sourceOwner: string; key: string; reason: string }[];
  evidenceRefs: readonly string[];
  writeActions: readonly unknown[];
  providerActions: readonly unknown[];
};

type FinalSnapshot = CasaNativaAdminSnapshot & {
  preorder: PreorderProjection;
  management: ManagementProjection;
};

const money = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});
const number = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 2 });

function formatMoney(minor: number): string {
  return money.format(minor / 100);
}
function formatTime(value: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Argentina/Buenos_Aires",
  }).format(new Date(value));
}
function factValue(fact: OperationalFact): string {
  if (!fact.value) return "—";
  if (fact.value.kind === "count") return number.format(fact.value.value);
  return `${number.format(fact.value.amount / 10 ** fact.value.scale)} ${fact.value.unit}`;
}
function Status({ value }: { value: string }) {
  const good = ["open", "confirmed", "measured", "complete"].includes(value);
  return <span className={`casa-v2-status ${good ? "good" : "warn"}`}>{value.replaceAll("_", " ")}</span>;
}

export function CasaNativaPreorderSection({ snapshot }: { snapshot: CasaNativaAdminSnapshot }) {
  const { preorder } = snapshot as FinalSnapshot;
  return <>
    <div className="casa-v2-heading"><div><p className="casa-v2-kicker">Preorder canónico</p><h2>Preventa y capacidad</h2><p>La reserva consume capacidad de un slot real y está coordinada con un Order existente. No representa una reserva de mesa.</p></div><Status value={preorder.reservation.status} /></div>
    <div className="casa-v2-metrics">
      <article className="casa-v2-metric"><span>Servicio</span><strong>{formatTime(preorder.slot.serviceAt)}</strong><small>Corte {formatTime(preorder.slot.cutoffAt)}</small></article>
      <article className="casa-v2-metric"><span>Capacidad ocupada</span><strong>{preorder.slot.decision.activeOrders} pedido</strong><small>{preorder.slot.decision.activeUnits} unidades</small></article>
      <article className="casa-v2-metric"><span>Capacidad restante</span><strong>{preorder.slot.decision.remainingOrders ?? "∞"}</strong><small>{preorder.slot.decision.remainingUnits ?? "∞"} unidades</small></article>
      <article className="casa-v2-metric"><span>Estado del slot</span><strong>{preorder.slot.decision.status}</strong><small>{preorder.slot.decision.reason}</small></article>
    </div>
    <div className="casa-v2-grid">
      <section className="casa-v2-card"><header><div><p className="casa-v2-kicker">Reserva de capacidad</p><h3>{preorder.reservation.id}</h3></div><Status value={preorder.reservation.status} /></header><div className="casa-v2-canonical"><div><span>Order real</span><strong>{preorder.reservation.orderId}</strong></div><div><span>Unidades</span><strong>{preorder.reservation.units}</strong></div><div><span>Confirmada</span><strong>{preorder.reservation.confirmedAt ? formatTime(preorder.reservation.confirmedAt) : "—"}</strong></div><div><span>Expira hold</span><strong>{formatTime(preorder.reservation.expiresAt)}</strong></div></div><div className="casa-v2-recipe-components">{preorder.reservation.itemRefs.map((ref) => <span key={`${ref.sourceModuleKey}:${ref.sourceItemId}`}>{ref.sourceModuleKey} · {ref.sourceItemId}</span>)}</div></section>
      <section className="casa-v2-card"><header><div><p className="casa-v2-kicker">Coordinación Orders</p><h3>{preorder.order.customer}</h3></div><Status value={preorder.order.status} /></header><div className="casa-v2-canonical"><div><span>Pedido</span><strong>{preorder.order.id}</strong></div><div><span>Fulfillment</span><strong>{preorder.order.fulfillmentType}</strong></div><div><span>Total</span><strong>{formatMoney(preorder.order.totalMinor)}</strong></div><div><span>Contrato</span><strong>{preorder.coordination}</strong></div></div><p className="casa-v2-note">Preorder no posee el pedido: sólo reserva capacidad contra su GastroOrderId canónico.</p></section>
    </div>
  </>;
}

export function CasaNativaManagementSection({ snapshot }: { snapshot: CasaNativaAdminSnapshot }) {
  const { management } = snapshot as FinalSnapshot;
  const measured = management.operationalFacts.filter((fact) => fact.status === "measured").length;
  return <>
    <div className="casa-v2-heading"><div><p className="casa-v2-kicker">O9 · Management</p><h2>Gestión operativa</h2><p>Proyección read-only construida sobre facts de Analytics y evidencia tipada de Costing. No escribe dominios ni ejecuta proveedores.</p></div><small>{formatTime(management.window.startInclusive)} → {formatTime(management.window.endExclusive)}</small></div>
    <div className="casa-v2-metrics">
      <article className="casa-v2-metric"><span>Facts medidos</span><strong>{measured}/{management.operationalFacts.length}</strong><small>Analytics O9</small></article>
      <article className="casa-v2-metric"><span>Evidencia de costeo</span><strong>{management.costingEvidence.length}</strong><small>gastro.costing</small></article>
      <article className="casa-v2-metric"><span>Gaps</span><strong>{management.gaps.length}</strong><small>{management.attentionItems.length} requieren atención</small></article>
      <article className="casa-v2-metric"><span>Acciones</span><strong>{management.writeActions.length + management.providerActions.length}</strong><small>0 writes · 0 providers</small></article>
    </div>
    <div className="casa-v2-grid">
      <section className="casa-v2-card"><header><div><p className="casa-v2-kicker">Analytics</p><h3>Facts operativos</h3></div><strong>{management.evidenceRefs.length} evidencias</strong></header><div className="casa-v2-list">{management.operationalFacts.map((fact) => <div className="casa-v2-row" key={fact.factKey}><div><strong>{fact.factKey}</strong><small>{fact.sourceObservation.sourceOwner}</small></div><div><strong>{factValue(fact)}</strong><small>{fact.evidenceRefs.length} refs</small></div><div><Status value={fact.status} /></div><div /></div>)}</div></section>
      <section className="casa-v2-card"><header><div><p className="casa-v2-kicker">Costing</p><h3>Evidencia canónica</h3></div></header>{management.costingEvidence.map((entry, index) => <div className="casa-v2-receipt" key={`${entry.kind}:${entry.recipeVersionId ?? index}`}><div><strong>{entry.kind.replaceAll("_", " ")}</strong> <Status value={entry.status} /></div><small>{entry.recipeVersionId ?? entry.reason ?? "—"}</small>{entry.totalMinor == null ? null : <strong>{formatMoney(entry.totalMinor)}</strong>}</div>)}</section>
    </div>
    {management.gaps.length ? <section className="casa-v2-card"><header><div><p className="casa-v2-kicker">Atención</p><h3>Fuentes incompletas</h3></div></header>{management.gaps.map((gap) => <div className="casa-v2-receipt" key={`${gap.sourceOwner}:${gap.key}`}><strong>{gap.sourceOwner}</strong><small>{gap.key} · {gap.reason}</small></div>)}</section> : <p className="casa-v2-note">La proyección actual no tiene gaps de fuente. Management sigue siendo deliberadamente read-only.</p>}
  </>;
}
